import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import FaceDetectionOval from './FaceDetectionOval';
import VitalSignsRecording from './VitalSignsRecording';
import { recordingAPI } from '../services/api/api';
import { vitalSignsAPI } from '../services/api/vitalSignsStorageAPI';
import RecordRTC from 'recordrtc';
import { 
  Play, 
  Square, 
  Camera, 
  Mic, 
  CheckCircle, 
  AlertCircle,
  Heart,
  Timer,
  Upload,
  ArrowRight,
  ArrowLeft,
  FileText,
  SkipForward,
  Activity
} from 'lucide-react';
import toast from 'react-hot-toast';

const PatientRecordingNew = () => {
  console.log('🎬 PatientRecordingNew component loaded - NEW VERSION WITH 15s COUNTDOWN');
  const { token } = useParams();
  const navigate = useNavigate();
  
  // Session state
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  
  // Recording state
  const [recording, setRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [videoBlob, setVideoBlob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [completionStatus, setCompletionStatus] = useState(null);
  
  // Vital Signs Storage state
  const [sessionId, setSessionId] = useState(null);
  const [storedRecordings, setStoredRecordings] = useState([]);
  const [currentStep, setCurrentStep] = useState(0); // 0: video, 1: pulse, 2: vowel
  const [recordingMetadata, setRecordingMetadata] = useState({
    video: null,
    pulse: null,
    vowel: null
  });
  
  // Camera state
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  
  // UI state
  const [step, setStep] = useState('instructions'); // 'instructions', 'questionnaire', 'complete'
  const [finalVitals, setFinalVitals] = useState(null);
  const [vitalSignsMode, setVitalSignsMode] = useState(true);
  const [audioLevel, setAudioLevel] = useState(0);
  const [frequencyData, setFrequencyData] = useState([]);

  const videoRef = useRef(null);
  const audioRecorderRef = useRef(null);
  const videoRecorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const microphoneRef = useRef(null);

  const [recordingRequestId, setRecordingRequestId] = useState(null);

  // Load session data on mount
  useEffect(() => {
    loadSessionData();
  }, [token]);

  // Timer effect for recording - 15 second countdown
  useEffect(() => {
    if (recording) {
      const duration = 15; // Fixed 15 second duration
      setRecordingTime(duration);
      
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev <= 1) {
            stopRecording();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [recording]);

  // Audio analysis effect for vital signs mode
  useEffect(() => {
    if (recording && vitalSignsMode) {
      const currentQuestion = getCurrentQuestion();
      if (currentQuestion && currentQuestion.question_type === 'audio_only') {
        startAudioAnalysis();
      }
    } else {
      stopAudioAnalysis();
    }
  }, [recording, vitalSignsMode]);

  // Comprehensive error suppression to prevent infinite loop display
  useEffect(() => {
    // Store original error handlers
    const originalOnError = window.onerror;
    const originalOnUnhandledRejection = window.onunhandledrejection;
    
    // Track RecordRTC errors to implement circuit breaker
    let recordRTCErrorCount = 0;
    const maxRecordRTCErrors = 10; // Stop after 10 errors
    
    // Track suppressed errors for circuit breaker
    let suppressedErrorCount = 0;
    const maxSuppressedErrors = 5; // Trigger circuit breaker after 5 suppressed errors
    
    // Override global error handler
    window.onerror = (message, source, lineno, colno, error) => {
      // Suppress specific RecordRTC state errors
      if (message && typeof message === 'string' && 
          message.includes('Cannot read properties of undefined (reading \'state\')')) {
        
        recordRTCErrorCount++;
        console.warn(`🚫 Suppressed global error to prevent infinite loop display (${recordRTCErrorCount}/${maxRecordRTCErrors}):`, message);
        
        // IMMEDIATE CIRCUIT BREAKER: If this is a resize-triggered error, disable immediately
        if (message.includes('resize-triggered error')) {
          console.warn('🚫 IMMEDIATE CIRCUIT BREAKER: Resize-triggered error detected, disabling face detection immediately');
          window.dispatchEvent(new CustomEvent('disableFaceDetection', { 
            detail: { reason: 'Resize-triggered error detected' } 
          }));
        }
        
        // ULTRA AGGRESSIVE: Disable face detection after just 3 errors
        if (recordRTCErrorCount >= 3) {
          console.warn('🚫 ULTRA AGGRESSIVE CIRCUIT BREAKER: 3+ errors detected, disabling face detection immediately');
          window.dispatchEvent(new CustomEvent('disableFaceDetection', { 
            detail: { reason: 'Ultra aggressive circuit breaker triggered' } 
          }));
          recordRTCErrorCount = 0; // Reset counter
        }
        
        return true; // Prevent default error handling
      }
      
      // Call original handler for other errors
      if (originalOnError) {
        return originalOnError(message, source, lineno, colno, error);
      }
      return false;
    };
    
    // Override unhandled rejection handler
    window.onunhandledrejection = (event) => {
      // Suppress specific RecordRTC state errors
      if (event.reason && event.reason.message && 
          event.reason.message.includes('Cannot read properties of undefined (reading \'state\')')) {
        console.warn('🚫 Suppressed unhandled rejection to prevent infinite loop display:', event.reason);
        event.preventDefault(); // Prevent default error handling
        return;
      }
      
      // Call original handler for other rejections
      if (originalOnUnhandledRejection) {
        originalOnUnhandledRejection(event);
      }
    };
    
    // Override console.error to suppress specific errors
    const originalConsoleError = console.error;
    console.error = (...args) => {
      const errorMessage = args.join(' ');
      if (errorMessage.includes('Cannot read properties of undefined (reading \'state\')')) {
        console.warn('🚫 Suppressed console error to prevent infinite loop display:', ...args);
        return;
      }
      originalConsoleError.apply(console, args);
    };
    
    // Override console.warn to suppress specific warnings
    const originalConsoleWarn = console.warn;
    console.warn = (...args) => {
      const warningMessage = args.join(' ');
      if (warningMessage.includes('Cannot read properties of undefined (reading \'state\')')) {
        console.log('🚫 Suppressed console warning to prevent infinite loop display:', ...args);
        return;
      }
      originalConsoleWarn.apply(console, args);
    };

    // Aggressive React development error overlay suppression
    if (process.env.NODE_ENV === 'development') {
      // Override the error overlay trigger more aggressively
      const originalConsoleErrorForOverlay = console.error;
      console.error = (...args) => {
        const errorMessage = args.join(' ');
        if (errorMessage.includes('Cannot read properties of undefined (reading \'state\')')) {
          console.warn('🚫 Suppressed error overlay trigger:', ...args);
          return;
        }
        originalConsoleErrorForOverlay.apply(console, args);
      };

      // Completely disable React error overlay for specific errors
      try {
        // Override webpack hot reload error handling
        if (window.webpack && window.webpack.hot) {
          const originalHotReload = window.webpack.hot.accept;
          if (originalHotReload) {
            window.webpack.hot.accept = function(...args) {
              // Suppress hot reload errors for specific RecordRTC errors
              console.warn('🚫 Suppressed webpack hot reload for RecordRTC errors');
              return;
            };
          }
        }

        // Override React DevTools error reporting
        if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
          const originalEmit = window.__REACT_DEVTOOLS_GLOBAL_HOOK__.emit;
          if (originalEmit) {
            window.__REACT_DEVTOOLS_GLOBAL_HOOK__.emit = (eventName, ...args) => {
              if (eventName === 'error' && args[0] && args[0].message && 
                  args[0].message.includes('Cannot read properties of undefined (reading \'state\')')) {
                console.warn('🚫 Suppressed React DevTools error:', args[0]);
                return;
              }
              originalEmit(eventName, ...args);
            };
          }
        }

        // Override ErrorEvent constructor to prevent error overlay
        const originalErrorEvent = window.ErrorEvent;
        if (originalErrorEvent) {
          window.ErrorEvent = function(message, options) {
            if (message && typeof message === 'string' && 
                message.includes('Cannot read properties of undefined (reading \'state\')')) {
              console.warn('🚫 Suppressed ErrorEvent creation for state error');
              // Create a harmless error event instead
              return new originalErrorEvent('Suppressed error', { ...options, error: new Error('Suppressed') });
            }
            return new originalErrorEvent(message, options);
          };
          // Copy prototype and static properties
          Object.setPrototypeOf(window.ErrorEvent, originalErrorEvent);
          Object.getOwnPropertyNames(originalErrorEvent).forEach(prop => {
            if (typeof originalErrorEvent[prop] === 'function') {
              window.ErrorEvent[prop] = originalErrorEvent[prop].bind(originalErrorEvent);
            } else {
              window.ErrorEvent[prop] = originalErrorEvent[prop];
            }
          });
        }

        // Override window.addEventListener for error events
        const originalAddEventListener = window.addEventListener;
        window.addEventListener = function(type, listener, options) {
          if (type === 'error' || type === 'unhandledrejection') {
            // Wrap the listener to suppress specific errors
            const wrappedListener = function(event) {
              if (event && event.error && event.error.message && 
                  event.error.message.includes('Cannot read properties of undefined (reading \'state\')')) {
                console.warn('🚫 Suppressed event listener error:', event.error);
                return;
              }
              if (event && event.reason && event.reason.message && 
                  event.reason.message.includes('Cannot read properties of undefined (reading \'state\')')) {
                console.warn('🚫 Suppressed event listener rejection:', event.reason);
                return;
              }
              listener(event);
            };
            return originalAddEventListener.call(this, type, wrappedListener, options);
          }
          return originalAddEventListener.call(this, type, listener, options);
        };
      } catch (e) {
        console.warn('Could not override React error overlay mechanisms:', e);
      }
    }
    
    // Mobile/responsive specific error suppression
    const handleResize = () => {
      // Force suppress any pending errors when resizing
      if (window.onerror) {
        const currentOnError = window.onerror;
        window.onerror = (message, source, lineno, colno, error) => {
          if (message && typeof message === 'string' && 
              message.includes('Cannot read properties of undefined (reading \'state\')')) {
            console.warn('🚫 Suppressed resize-triggered error:', message);
            return true;
          }
          return currentOnError(message, source, lineno, colno, error);
        };
      }
    };

    // Listen for resize events to suppress errors during responsive changes
    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      // Restore original handlers
      window.onerror = originalOnError;
      window.onunhandledrejection = originalOnUnhandledRejection;
      console.error = originalConsoleError;
      console.warn = originalConsoleWarn;
      
      // Remove resize listeners
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
      
      // Restore original addEventListener if overridden
      if (window.addEventListener.__original) {
        window.addEventListener = window.addEventListener.__original;
      }
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanupCamera();
      cleanupRecordRTC();
    };
  }, []);

  const loadSessionData = async () => {
    try {
      console.log('🔄 Loading session data for token:', token);
      const response = await recordingAPI.getRecordingSession(token);
      const data = response.data;
      
      console.log('✅ Session data loaded:', data);
      console.log('🔍 DEBUG: Session data details:', {
        sessionId: data.session_id,
        patientId: data.patient_id,
        patientName: data.patient_name,
        questionsCount: data.questions?.length,
        totalQuestions: data.total_questions,
        instructions: data.instructions?.length,
        recordingRequestId: data.recording_request_id
      });
      console.log('🔍 DEBUG: Full session data:', JSON.stringify(data, null, 2));
      console.log('🔍 DEBUG: Patient ID from session data:', data.patient_id);
      console.log('🔍 DEBUG: Patient ID type:', typeof data.patient_id);
      
      setSessionData(data);
      setRecordingRequestId(data.recording_request_id || null);
      setSessionId(data.session_id); // Set session ID for storage
      setQuestions(data.questions || []);
      setCompletionStatus({ 
        total_questions: data.total_questions || 0,
        completed_questions: 0,
        is_complete: false,
        completion_percentage: 0
      });
      setLoading(false);
      
      console.log('🔍 DEBUG: Component state updated:', {
        questionsLoaded: (data.questions || []).length,
        loading: false,
        sessionId: data.session_id
      });
    } catch (error) {
      console.error('❌ Load session error:', error);
      console.error('🔍 DEBUG: Load session error details:', {
        name: error.name,
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      toast.error('Recording session not found or expired');
      setTimeout(() => navigate('/'), 3000);
    }
  };

  // Add WebSocket connection status check
  useEffect(() => {
    const checkWebSocketConnection = async () => {
      try {
        console.log('🔍 Checking backend connectivity...');
        
        // Test backend connectivity with timeout
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
        
        const response = await fetch('/api/health', { 
          signal: controller.signal,
          method: 'GET'
        });
        
        clearTimeout(timeoutId);
        
        if (!response.ok) {
          console.warn('⚠️ Backend connectivity issues detected:', response.status, response.statusText);
          setCameraError('Backend connection issues. Please try again.');
          toast.error('Backend connection problems detected');
        } else {
          console.log('✅ Backend connectivity check passed');
          // Clear any previous camera errors related to connectivity
          if (cameraError && cameraError.includes('Backend connection')) {
            setCameraError(null);
          }
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.warn('⚠️ Backend connectivity check timed out');
          setCameraError('Backend connection timeout. Please check your internet connection.');
          toast.error('Backend connection timeout');
        } else {
          console.warn('⚠️ Backend connectivity check failed:', error);
          setCameraError('Backend connection failed. Please try again.');
          toast.error('Backend connection failed');
        }
      }
    };
    
    // Check immediately and then every 30 seconds
    checkWebSocketConnection();
    const intervalId = setInterval(checkWebSocketConnection, 30000);
    
    return () => {
      clearInterval(intervalId);
    };
  }, [cameraError]);

  const cleanupCamera = () => {
    console.log('🧹 Cleaning up camera...');
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
        console.log(`Stopped ${track.kind} track`);
      });
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    setCameraReady(false);
    setCameraError(null);
  };

  const cleanupRecordRTC = () => {
    console.log('🧹 Cleaning up RecordRTC...');
    
    if (audioRecorderRef.current) {
      try {
        // Check if recorder still exists and has getState method
        if (audioRecorderRef.current && 
            typeof audioRecorderRef.current.getState === 'function' &&
            audioRecorderRef.current.getState() === 'recording') {
          audioRecorderRef.current.stopRecording();
        }
        audioRecorderRef.current.destroy();
      } catch (e) {
        console.warn('Error cleaning audio recorder:', e);
      }
      audioRecorderRef.current = null;
    }
    
    if (videoRecorderRef.current) {
      try {
        // Check if recorder still exists and has getState method
        if (videoRecorderRef.current && 
            typeof videoRecorderRef.current.getState === 'function' &&
            videoRecorderRef.current.getState() === 'recording') {
          videoRecorderRef.current.stopRecording();
        }
        videoRecorderRef.current.destroy();
      } catch (e) {
        console.warn('Error cleaning video recorder:', e);
      }
      videoRecorderRef.current = null;
    }
  };

  const startAudioAnalysis = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      microphoneRef.current = stream;

      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 256;

      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const updateFrequency = () => {
        if (!recording) return;

        analyserRef.current.getByteFrequencyData(dataArray);
        
        // Calculate average frequency and audio level
        const average = dataArray.reduce((a, b) => a + b) / bufferLength;
        setAudioLevel(average);
        
        // Update frequency data for visualization
        setFrequencyData(prev => [...prev.slice(-50), average]);

        requestAnimationFrame(updateFrequency);
      };

      updateFrequency();
    } catch (error) {
      console.error('Audio analysis error:', error);
    }
  };

  const stopAudioAnalysis = () => {
    if (microphoneRef.current) {
      microphoneRef.current.getTracks().forEach(track => track.stop());
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
    }
    setFrequencyData([]);
    setAudioLevel(0);
  };

  const initializeCamera = async (retryCount = 0) => {
    const currentQuestion = getCurrentQuestion();
    if (!currentQuestion) {
      console.log('🔍 DEBUG: No current question found');
      return;
    }

    console.log('🔍 DEBUG: Initializing camera for question:', currentQuestion);
    console.log('🔍 DEBUG: Question type:', currentQuestion.question_type);
    console.log('🔍 DEBUG: Video ref current:', videoRef.current);
    console.log('🔍 DEBUG: Camera ready state before init:', cameraReady);
    console.log('🔍 DEBUG: Retry count:', retryCount);

    // If video element not ready and we haven't retried too many times, retry
    if (!videoRef.current && currentQuestion.question_type !== 'audio_only' && retryCount < 5) {
      console.log(`🔍 DEBUG: Video element not ready (attempt ${retryCount + 1}/5), retrying in 500ms...`);
      setTimeout(() => {
        initializeCamera(retryCount + 1);
      }, 500);
      return;
    }

    // If we've exhausted retries and still no video element, show error
    if (!videoRef.current && currentQuestion.question_type !== 'audio_only' && retryCount >= 5) {
      console.error('🔍 DEBUG: Video element not found after all retries! Component may not be properly mounted.');
      setCameraError('Video element not available. Please refresh the page and try again.');
      toast.error('Camera initialization failed. Please refresh the page.');
      return;
    }

    // Skip camera for audio-only questions
    if (currentQuestion.question_type === 'audio_only') {
      console.log('🔍 DEBUG: Audio-only question, skipping camera');
      setCameraReady(true);
      return;
    }

    try {
      setCameraError(null);
      console.log('🎥 Initializing camera for:', currentQuestion.question_type);

      const constraints = {
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user'
        },
        audio: true
      };

      console.log('🔍 DEBUG: Camera constraints:', constraints);
      console.log('🔍 DEBUG: Navigator.mediaDevices available:', !!navigator.mediaDevices);
      console.log('🔍 DEBUG: getUserMedia available:', !!navigator.mediaDevices?.getUserMedia);

      console.log('🎥 Requesting camera access...');
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      console.log('✅ Camera access granted');
      console.log('🔍 DEBUG: Stream object:', stream);
      console.log('🔍 DEBUG: Stream active:', stream.active);
      console.log('🔍 DEBUG: Stream tracks:', stream.getTracks().map(track => ({
        kind: track.kind,
        label: track.label,
        enabled: track.enabled,
        readyState: track.readyState
      })));
      
      streamRef.current = stream;
      console.log('🔍 DEBUG: Stream stored in ref:', !!streamRef.current);

      // Double-check video element exists before proceeding
      if (videoRef.current) {
        console.log('🔍 DEBUG: Video element found, setting srcObject');
        console.log('🔍 DEBUG: Video element before:', {
          srcObject: videoRef.current.srcObject,
          videoWidth: videoRef.current.videoWidth,
          videoHeight: videoRef.current.videoHeight,
          readyState: videoRef.current.readyState
        });

        videoRef.current.srcObject = stream;
        console.log('🔍 DEBUG: srcObject set to stream');
        
        videoRef.current.muted = true;
        console.log('🔍 DEBUG: Video muted set to true');

        // Add event listeners for debugging
        videoRef.current.onloadedmetadata = () => {
          console.log('🔍 DEBUG: Video metadata loaded');
          console.log('🔍 DEBUG: Video dimensions:', {
            videoWidth: videoRef.current.videoWidth,
            videoHeight: videoRef.current.videoHeight
          });
        };

        videoRef.current.oncanplay = () => {
          console.log('🔍 DEBUG: Video can play');
        };

        // Remove problematic onplaying handler that causes race conditions
        // videoRef.current.onplaying = () => {
        //   console.log('🔍 DEBUG: Video is playing');
        // };

        videoRef.current.onerror = (error) => {
          console.error('🔍 DEBUG: Video element error:', error);
        };

        console.log('🔍 DEBUG: About to call video.play()');
        await videoRef.current.play();
        console.log('✅ Camera preview started');
        
        console.log('🔍 DEBUG: Video element after play:', {
          srcObject: !!videoRef.current.srcObject,
          videoWidth: videoRef.current.videoWidth,
          videoHeight: videoRef.current.videoHeight,
          readyState: videoRef.current.readyState,
          paused: videoRef.current.paused,
          muted: videoRef.current.muted
        });
      } else {
        console.error('🔍 DEBUG: Video element not found after retries! This is the core issue.');
        console.error('🔍 DEBUG: Question type requiring video:', currentQuestion.question_type);
        console.error('🔍 DEBUG: Current step:', step);
        console.error('🔍 DEBUG: Current question index:', currentQuestionIndex);
        throw new Error('Video element not available for camera initialization');
      }

      setCameraReady(true);
      console.log('🔍 DEBUG: Camera ready state set to true');
    } catch (error) {
      console.error('❌ Camera initialization failed:', error);
      console.error('🔍 DEBUG: Error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      
      let errorMessage = 'Camera access failed';
      if (error.name === 'NotAllowedError') {
        errorMessage = 'Camera permission denied. Please allow camera access.';
      } else if (error.name === 'NotFoundError') {
        errorMessage = 'No camera found. Please connect a camera.';
      } else if (error.name === 'NotReadableError') {
        errorMessage = 'Camera is busy. Please close other applications using the camera.';
      } else if (error.message === 'Video element not available for camera initialization') {
        errorMessage = 'Video component not ready. Please refresh the page and try again.';
      }
      
      setCameraError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const startRecording = async () => {
    const currentQuestion = getCurrentQuestion();
    if (!currentQuestion) {
      console.log('🔍 DEBUG: No current question for recording');
      return;
    }

    try {
      setRecording(true);
      setRecordingTime(0);
      console.log('🎬 Starting recording for:', currentQuestion.question_type);
      console.log('🔍 DEBUG: Current stream ref:', !!streamRef.current);
      console.log('🔍 DEBUG: Stream active:', streamRef.current?.active);

      // Use existing stream or get new one
      let stream = streamRef.current;
      if (!stream) {
        console.log('🔍 DEBUG: No existing stream, requesting new one');
        const constraints = {
          video: currentQuestion.question_type !== 'audio_only',
          audio: true
        };
        console.log('🔍 DEBUG: New stream constraints:', constraints);
        stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;
        console.log('🔍 DEBUG: New stream obtained:', !!stream);
      }

      console.log('🔍 DEBUG: Stream tracks for recording:', stream.getTracks().map(track => ({
        kind: track.kind,
        label: track.label,
        enabled: track.enabled,
        readyState: track.readyState
      })));

      // Create RecordRTC instances
      try {
        if (currentQuestion.question_type === 'audio_video') {
        console.log('🔍 DEBUG: Creating audio+video recorders');
        
        // Video recorder (includes audio)
        const videoRecorderOptions = {
          type: 'video',
          mimeType: 'video/webm;codecs=vp8,opus',
          audioBitsPerSecond: 128000,
          videoBitsPerSecond: 1000000,
          canvas: { width: 640, height: 480 },
          frameInterval: 90
        };
        console.log('🔍 DEBUG: Video recorder options:', videoRecorderOptions);
        
                 const videoRecorder = new RecordRTC(stream, videoRecorderOptions);
         videoRecorderRef.current = videoRecorder;
         
         // Add error handler for video recorder
         videoRecorder.onerror = (error) => {
           console.error('❌ Video recorder error:', error);
           toast.error('Video recording error occurred');
         };
         
         console.log('🔍 DEBUG: About to start video recording');
        videoRecorder.startRecording();
        console.log('🔍 DEBUG: Video recording started');
        
        // Separate audio recorder for better compatibility
        const audioRecorderOptions = {
          type: 'audio',
          mimeType: 'audio/webm;codecs=opus',
          audioBitsPerSecond: 128000
        };
        console.log('🔍 DEBUG: Audio recorder options:', audioRecorderOptions);
        
                 const audioRecorder = new RecordRTC(stream, audioRecorderOptions);
         audioRecorderRef.current = audioRecorder;
         
         // Add error handler for audio recorder
         audioRecorder.onerror = (error) => {
           console.error('❌ Audio recorder error:', error);
           toast.error('Audio recording error occurred');
         };
         
         console.log('🔍 DEBUG: About to start audio recording');
        audioRecorder.startRecording();
        console.log('🔍 DEBUG: Audio recording started');

      } else if (currentQuestion.question_type === 'audio_only') {
        console.log('🔍 DEBUG: Creating audio-only recorder');
        
        // Audio only
        const audioRecorderOptions = {
          type: 'audio',
          mimeType: 'audio/webm;codecs=opus',
          audioBitsPerSecond: 128000
        };
        console.log('🔍 DEBUG: Audio-only recorder options:', audioRecorderOptions);
        
                 const audioRecorder = new RecordRTC(stream, audioRecorderOptions);
         audioRecorderRef.current = audioRecorder;
         
         // Add error handler for audio-only recorder
         audioRecorder.onerror = (error) => {
           console.error('❌ Audio-only recorder error:', error);
           toast.error('Audio recording error occurred');
         };
         
         console.log('🔍 DEBUG: About to start audio-only recording');
        audioRecorder.startRecording();
        console.log('🔍 DEBUG: Audio-only recording started');

      } else if (currentQuestion.question_type === 'video_only') {
        console.log('🔍 DEBUG: Creating video-only recorder');
        
        // Video only (no audio)
        const videoRecorderOptions = {
          type: 'video',
          mimeType: 'video/webm;codecs=vp8',
          videoBitsPerSecond: 1000000,
          canvas: { width: 640, height: 480 }
        };
        console.log('🔍 DEBUG: Video-only recorder options:', videoRecorderOptions);
        
                 const videoRecorder = new RecordRTC(stream, videoRecorderOptions);
         videoRecorderRef.current = videoRecorder;
         
         // Add error handler for video-only recorder
         videoRecorder.onerror = (error) => {
           console.error('❌ Video-only recorder error:', error);
           toast.error('Video recording error occurred');
         };
         
         console.log('🔍 DEBUG: About to start video-only recording');
                 videoRecorder.startRecording();
         console.log('🔍 DEBUG: Video-only recording started');
       }
     } catch (recorderError) {
       console.error('❌ RecordRTC creation error:', recorderError);
       throw new Error(`Failed to create recording instances: ${recorderError.message}`);
     }

      console.log('✅ Recording started successfully');
      console.log('🔍 DEBUG: Recording state:', {
        recording: true,
        audioRecorder: !!audioRecorderRef.current,
        videoRecorder: !!videoRecorderRef.current,
        stream: !!streamRef.current
      });
      
      // Timer is now handled by the useEffect with 15-second countdown

    } catch (error) {
      console.error('❌ Start recording failed:', error);
      console.error('🔍 DEBUG: Start recording error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      toast.error('Failed to start recording: ' + error.message);
      setRecording(false);
    }
  };

  const stopRecording = async () => {
    if (!recording) {
      console.log('🔍 DEBUG: Stop recording called but not currently recording');
      return;
    }

    try {
      setRecording(false);
      setUploading(true);
      console.log('🛑 Stopping recording...');
      console.log('🔍 DEBUG: Recording state before stop:', {
        audioRecorder: !!audioRecorderRef.current,
        videoRecorder: !!videoRecorderRef.current,
        audioRecorderState: audioRecorderRef.current?.getState(),
        videoRecorderState: videoRecorderRef.current?.getState()
      });

      const currentQuestion = getCurrentQuestion();
      console.log('🔍 DEBUG: Current question for upload:', currentQuestion?.question_id);
      
      // Stop recorders and get blobs
      console.log('🔍 DEBUG: About to stop audio recorder');
      const audioBlob = await stopAudioRecorder();
      console.log('🔍 DEBUG: Audio blob result:', {
        blob: !!audioBlob,
        size: audioBlob?.size,
        type: audioBlob?.type
      });

      console.log('🔍 DEBUG: About to stop video recorder');
      const videoBlob = await stopVideoRecorder();
      console.log('🔍 DEBUG: Video blob result:', {
        blob: !!videoBlob,
        size: videoBlob?.size,
        type: videoBlob?.type
      });

      console.log('✅ Recording stopped, uploading to storage...');

      // Upload to storage based on question type
      if (currentQuestion.question_type === 'audio_video') {
        if (videoBlob) {
          await uploadVideoRecordingWithRetry(videoBlob, {
            questionId: currentQuestion.question_id,
            questionText: currentQuestion.question_text
          });
        }
        if (audioBlob) {
          await uploadPulseAudioWithRetry(audioBlob, {
            questionId: currentQuestion.question_id,
            questionText: currentQuestion.question_text
          });
        }
      } else if (currentQuestion.question_type === 'audio_only') {
        if (audioBlob) {
          // Determine if it's pulse or vowel audio based on question text
          const isPulseAudio = currentQuestion.question_text.toLowerCase().includes('pulse') || 
                              currentQuestion.question_text.toLowerCase().includes('heart') ||
                              currentQuestion.question_text.toLowerCase().includes('chest');
          
          if (isPulseAudio) {
            await uploadPulseAudioWithRetry(audioBlob, {
              questionId: currentQuestion.question_id,
              questionText: currentQuestion.question_text
            });
          } else {
            await uploadVowelAudioWithRetry(audioBlob, {
              questionId: currentQuestion.question_id,
              questionText: currentQuestion.question_text
            });
          }
        }
      } else if (currentQuestion.question_type === 'video_only') {
        if (videoBlob) {
          await uploadVideoRecordingWithRetry(videoBlob, {
            questionId: currentQuestion.question_id,
            questionText: currentQuestion.question_text
          });
        }
      }
      
      // Move to next question or complete
      await moveToNextQuestion();

    } catch (error) {
      console.error('❌ Stop recording failed:', error);
      console.error('🔍 DEBUG: Stop recording error details:', {
        name: error.name,
        message: error.message,
        stack: error.stack
      });
      toast.error('Failed to save recording: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const stopAudioRecorder = () => {
    return new Promise((resolve) => {
      if (audioRecorderRef.current) {
        console.log('🔍 DEBUG: Stopping audio recorder, state:', audioRecorderRef.current.getState());
        audioRecorderRef.current.stopRecording(() => {
          const blob = audioRecorderRef.current.getBlob();
          console.log('✅ Audio blob ready:', blob.size, 'bytes, type:', blob.type);
          resolve(blob);
        });
      } else {
        console.log('🔍 DEBUG: No audio recorder to stop');
        resolve(null);
      }
    });
  };

  const stopVideoRecorder = () => {
    return new Promise((resolve) => {
      if (videoRecorderRef.current) {
        console.log('🔍 DEBUG: Stopping video recorder, state:', videoRecorderRef.current.getState());
        videoRecorderRef.current.stopRecording(() => {
          const blob = videoRecorderRef.current.getBlob();
          console.log('✅ Video blob ready:', blob.size, 'bytes, type:', blob.type);
          resolve(blob);
        });
      } else {
        console.log('🔍 DEBUG: No video recorder to stop');
        resolve(null);
      }
    });
  };



  // Vital Signs Storage Functions
  const uploadVideoRecording = async (videoBlob, metadata = {}) => {
    if (!sessionId || !videoBlob) {
      console.error('❌ Missing sessionId or videoBlob for upload');
      return null;
    }

    try {
      console.log('📹 Uploading video recording to storage...');
      const response = await vitalSignsAPI.uploadVideoRecording(sessionId, videoBlob, {
        ...metadata,
        duration: recordingTime,
        step: 1,
        recordingType: 'video'
      });

      console.log('✅ Video recording uploaded successfully:', response.data);
      
      const recordingInfo = {
        type: 'video',
        filePath: response.data.file_path,
        duration: recordingTime,
        timestamp: new Date().toISOString(),
        metadata: metadata
      };

      setStoredRecordings(prev => [...prev, recordingInfo]);
      setRecordingMetadata(prev => ({ ...prev, video: recordingInfo }));
      
      toast.success('Video recording saved successfully!');
      return recordingInfo;
    } catch (error) {
      console.error('❌ Video upload failed:', error);
      toast.error('Failed to save video recording');
      throw error;
    }
  };

  const uploadPulseAudio = async (audioBlob, metadata = {}) => {
    if (!sessionId || !audioBlob) {
      console.error('❌ Missing sessionId or audioBlob for upload');
      return null;
    }

    try {
      console.log('💓 Uploading pulse audio to storage...');
      const response = await vitalSignsAPI.uploadPulseAudio(sessionId, audioBlob, {
        ...metadata,
        duration: recordingTime,
        step: 2,
        recordingType: 'pulse_audio',
        audioAnalysis: {
          averageLevel: audioLevel,
          frequencyData: frequencyData.slice(-10) // Last 10 frequency readings
        }
      });

      console.log('✅ Pulse audio uploaded successfully:', response.data);
      
      const recordingInfo = {
        type: 'pulse_audio',
        filePath: response.data.file_path,
        duration: recordingTime,
        timestamp: new Date().toISOString(),
        metadata: metadata
      };

      setStoredRecordings(prev => [...prev, recordingInfo]);
      setRecordingMetadata(prev => ({ ...prev, pulse: recordingInfo }));
      
      toast.success('Pulse audio saved successfully!');
      return recordingInfo;
    } catch (error) {
      console.error('❌ Pulse audio upload failed:', error);
      toast.error('Failed to save pulse audio');
      throw error;
    }
  };

  const uploadVowelAudio = async (audioBlob, metadata = {}) => {
    if (!sessionId || !audioBlob) {
      console.error('❌ Missing sessionId or audioBlob for upload');
      return null;
    }

    try {
      console.log('🗣️ Uploading vowel audio to storage...');
      const response = await vitalSignsAPI.uploadVowelAudio(sessionId, audioBlob, {
        ...metadata,
        duration: recordingTime,
        step: 3,
        recordingType: 'vowel_audio',
        audioAnalysis: {
          averageLevel: audioLevel,
          frequencyData: frequencyData.slice(-10) // Last 10 frequency readings
        }
      });

      console.log('✅ Vowel audio uploaded successfully:', response.data);
      
      const recordingInfo = {
        type: 'vowel_audio',
        filePath: response.data.file_path,
        duration: recordingTime,
        timestamp: new Date().toISOString(),
        metadata: metadata
      };

      setStoredRecordings(prev => [...prev, recordingInfo]);
      setRecordingMetadata(prev => ({ ...prev, vowel: recordingInfo }));
      
      toast.success('Vowel audio saved successfully!');
      return recordingInfo;
    } catch (error) {
      console.error('❌ Vowel audio upload failed:', error);
      toast.error('Failed to save vowel audio');
      throw error;
    }
  };

  const completeVitalSignsSession = async () => {
    if (!sessionId) {
      console.error('❌ No session ID for completion');
      return;
    }

    try {
      console.log('🎉 Completing vital signs session with storage...');
      
      // Get the recording request ID from state or session data
      const rrid = recordingRequestId || sessionData?.recording_request_id || null;
      console.log('🔗 Recording request ID for completion:', rrid);
      console.log('🔍 DEBUG: Full session data in completion:', JSON.stringify(sessionData, null, 2));
      
      // Complete the session with all recordings and recording request ID
      const response = await vitalSignsAPI.completeVitalSignsSession(sessionId, storedRecordings, rrid);
      
      console.log('✅ Vital signs session completed with storage:', response.data);
      
      // Get final vitals from the response
      const vitals = response.data.vital_signs || response.data.vitals;
      setFinalVitals(vitals);
      setStep('complete');
      
      // Ensure the recording link is explicitly marked as completed using the token as well
      try {
        console.log('🛰️ Calling link-complete via recordingAPI...', { tokenPresent: !!token, sessionId });
        await recordingAPI.completeSession(token, sessionId);
        console.log('✅ Link completion via recordingAPI succeeded');
      } catch (e) {
        console.warn('⚠️ Link completion via recordingAPI failed, attempting fetch fallback:', e?.message || e);
        try {
          const fd = new FormData();
          fd.append('session_id', sessionId);
          const res = await fetch(`/api/recording/${token}/complete-session`, {
            method: 'POST',
            body: fd
          });
          console.log('🛰️ Fetch fallback link-complete status:', res.status);
        } catch (e2) {
          console.warn('❌ Fetch fallback for link completion also failed:', e2?.message || e2);
        }
      }

      // Cleanup everything
      cleanupCamera();
      cleanupRecordRTC();
      
      console.log('✅ Vital signs session completed successfully with storage');
      toast.success('Vital signs recording session completed!');
      
    } catch (error) {
      console.error('❌ Complete vital signs session failed:', error);
      toast.error('Failed to complete session: ' + error.message);
    }
  };

  const moveToNextQuestion = async () => {
    // Clean up current recording
    cleanupRecordRTC();
    
    if (currentQuestionIndex + 1 < questions.length) {
      // Move to next question
      setCurrentQuestionIndex(prev => prev + 1);
      setCameraReady(false);
      setCameraError(null);
      
      console.log('🔍 DEBUG: Moving to next question, will initialize camera after render');
      
    } else {
      // All questions completed
      await completeVitalSignsSession();
    }
  };

  // Effect to initialize camera when question changes
  useEffect(() => {
    if (step === 'questionnaire' && questions.length > 0) {
      console.log('🔍 DEBUG: Question changed effect triggered, index:', currentQuestionIndex);
      console.log('🔍 DEBUG: Video ref available:', !!videoRef.current);
      
      // Wait for DOM to update and video element to be mounted, then initialize camera
      const timeoutId = setTimeout(() => {
        console.log('🔍 DEBUG: Checking video element after DOM update');
        console.log('🔍 DEBUG: Video ref now available:', !!videoRef.current);
        
        // Only initialize camera if video element is available
        if (videoRef.current) {
          console.log('🔍 DEBUG: Video element found, initializing camera');
          initializeCamera();
        } else {
          console.log('🔍 DEBUG: Video element still not available, will retry in useEffect');
          // Set a flag to retry when video element becomes available
          setCameraReady(false);
        }
      }, 300); // Slightly longer delay to ensure DOM is updated
      
      return () => clearTimeout(timeoutId);
    }
  }, [currentQuestionIndex, step, questions.length]);

  // Additional effect to initialize camera when video element becomes available
  useEffect(() => {
    if (step === 'questionnaire' && questions.length > 0 && videoRef.current && !cameraReady && !cameraError) {
      console.log('🔍 DEBUG: Video element now available, initializing camera');
      initializeCamera();
    }
  }, [videoRef.current, step, questions.length, cameraReady, cameraError]);

  const completeSession = async () => {
    try {
      console.log('🎉 Completing session...');
      
      const response = await recordingAPI.completeSession(token, sessionData.session_id);
      const vitals = response.data.vital_signs;
      
      setFinalVitals(vitals);
      setStep('complete');
      
      // Scrub the token from the URL while keeping the results view visible
      try {
        if (window && window.history && window.history.replaceState) {
          window.history.replaceState(null, '', '/record-complete');
        }
      } catch (e) {
        // ignore URL replace errors
      }
      
      // Cleanup everything
      cleanupCamera();
      cleanupRecordRTC();
      
      console.log('✅ Session completed successfully');
      toast.success('Recording session completed!');
      
    } catch (error) {
      console.error('❌ Complete session failed:', error);
      toast.error('Failed to complete session: ' + error.message);
    }
  };

  const skipQuestion = () => {
    if (currentQuestionIndex + 1 < questions.length) {
      setCurrentQuestionIndex(prev => prev + 1);
      setCameraReady(false);
      setCameraError(null);
      setTimeout(() => initializeCamera(), 500);
    } else {
      completeVitalSignsSession();
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      setCameraReady(false);
      setCameraError(null);
      setTimeout(() => initializeCamera(), 500);
    }
  };

  const startQuestionnaire = () => {
    console.log('🔍 DEBUG: Starting questionnaire');
    console.log('🔍 DEBUG: Questions available:', questions.length);
    console.log('🔍 DEBUG: First question:', questions[0]);
    
    setStep('questionnaire');
    setCurrentQuestionIndex(0);
    
    console.log('🔍 DEBUG: Set step to questionnaire, camera will initialize via useEffect');
  };

  const getCurrentQuestion = () => {
    const question = questions[currentQuestionIndex];
    console.log('🔍 DEBUG: getCurrentQuestion called, index:', currentQuestionIndex, 'question:', question);
    return question;
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getQuestionTypeLabel = (type) => {
    switch (type) {
      case 'audio_video': return 'Audio + Video';
      case 'audio_only': return 'Audio Only';
      case 'video_only': return 'Video Only';
      default: return 'Recording';
    }
  };

  // Retry mechanism for failed operations
  const retryOperation = async (operation, maxRetries = 3, delay = 1000) => {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        console.warn(`⚠️ Operation failed (attempt ${attempt}/${maxRetries}):`, error);
        
        if (attempt === maxRetries) {
          throw error; // Re-throw on final attempt
        }
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      }
    }
  };

  // Enhanced upload functions with retry mechanism
  const uploadVideoRecordingWithRetry = async (videoBlob, metadata = {}) => {
    return retryOperation(() => uploadVideoRecording(videoBlob, metadata));
  };

  const uploadPulseAudioWithRetry = async (audioBlob, metadata = {}) => {
    return retryOperation(() => uploadPulseAudio(audioBlob, metadata));
  };

  const uploadVowelAudioWithRetry = async (audioBlob, metadata = {}) => {
    return retryOperation(() => uploadVowelAudio(audioBlob, metadata));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-lg font-medium text-gray-700">Loading recording session...</p>
        </div>
      </div>
    );
  }

  // Render Vital Signs Mode
  if (vitalSignsMode && step === 'questionnaire') {
    const vitalSignsProps = {
      token: token,
      recordingRequestId: recordingRequestId || sessionData?.recording_request_id || null,
      patientId: sessionData?.patient_id || null
    };
    console.log('🔍 DEBUG: Passing props to VitalSignsRecording:', vitalSignsProps);
    console.log('🔍 DEBUG: sessionData?.patient_id:', sessionData?.patient_id);
    console.log('🔍 DEBUG: sessionData exists:', !!sessionData);
    
    return <VitalSignsRecording 
      token={token}
      recordingRequestId={recordingRequestId || sessionData?.recording_request_id || null}
      patientId={sessionData?.patient_id || null}
    />;
  }

  if (!sessionData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Session Not Found</h1>
          <p className="text-gray-600">This recording session has expired or is invalid.</p>
        </div>
      </div>
    );
  }

  const progress = completionStatus ? (completionStatus.completed_questions / completionStatus.total_questions) * 100 : 0;
  const currentQuestion = getCurrentQuestion();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <div className="w-20 h-20 bg-white rounded-full shadow-lg flex items-center justify-center p-2">
              <img 
                src="/Vital-logo.png" 
                alt="Vital Audio Logo" 
                className="h-12 w-auto"
                onError={(e) => {
                  // Fallback to text if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
            </div>
            <div className="hidden">
              <Heart className="h-12 w-12 text-red-500 pulse-heart" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-1">Sonometrik</h1>
          <p className="text-lg text-gray-400 mb-2">by Vital Audio</p>
          
          
          {/* Progress Bar */}
          {step !== 'instructions' && questions.length > 0 && (
            <div className="mt-6 max-w-md mx-auto">
              <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>Progress</span>
                <span>{Math.round(progress)}% Complete</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>
          )}

          {/* Storage Status */}
          {step !== 'instructions' && storedRecordings.length > 0 && (
            <div className="mt-4 max-w-md mx-auto">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <h4 className="text-sm font-medium text-green-800 mb-2">Recordings Saved:</h4>
                <div className="space-y-1">
                  {storedRecordings.map((recording, index) => (
                    <div key={index} className="flex items-center text-xs text-green-700">
                      <CheckCircle className="h-3 w-3 mr-2" />
                      <span className="capitalize">{recording.type.replace('_', ' ')}</span>
                      <span className="ml-auto text-green-600">
                        {recording.duration}s
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Instructions Step */}
          {step === 'instructions' && (
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
                Track your Wellness
              </h2>
              


              {/* Vital Signs Mode Only */}
              <div className="text-center mb-6">
                <div className="inline-flex items-center px-4 py-2 bg-blue-100 rounded-lg">
                  {/* <Heart className="h-5 w-5 text-blue-600 mr-2" /> */}
                  <span className="text-blue-800 font-medium">Instructions</span>
                    </div>
                  </div>

              <div className="space-y-6 mb-8">
                {/* Step 1: Video Recording */}
                <div className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-blue-600 font-medium">1</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Step 1: Video Recording</h3>
                    </div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">Recording Tips:</h4>
                    <ul className="text-blue-800 text-sm space-y-1">
                      <li>• Make sure your face is well-lit and clearly visible.</li>
                    </ul>
                  </div>
                </div>

                {/* Step 2: Pulse Audio */}
                <div className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-blue-600 font-medium">2</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Step 2: Audio Capture</h3>
                    </div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">Recording Tips:</h4>
                    <ul className="text-blue-800 text-sm space-y-1">
                      <li>• Position the microphone slightly left of center on your chest</li>
                      <li>• Stay as still and quiet as possible</li>
                      <li>• Avoid background noise</li>
                    </ul>
                  </div>
                </div>

                {/* Step 3: Vowel Audio */}
                <div className="border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                      <span className="text-blue-600 font-medium">3</span>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Step 3: Voice Capture</h3>
                    </div>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 mb-2">Recording Tips:</h4>
                    <ul className="text-blue-800 text-sm space-y-1">
                      <li>• Hold your phone 6–8 inches from your mouth</li>
                      <li>• Maintain a consistent tone and volume</li>
                      <li>• Avoid speaking too softly or too loudly</li>
                    </ul>
                  </div>
                </div>
              </div>





              <div className="text-center">
                <button
                  onClick={startQuestionnaire}
                  className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 transition-colors flex items-center mx-auto"
                >
                  <FileText className="mr-3 h-6 w-6" />
                   Start Recording
                </button>
              </div>
            </div>
          )}

          {/* Questionnaire Step */}
          {step === 'questionnaire' && currentQuestion && (
            <div className="bg-white rounded-lg shadow-lg p-4">
              <div className="text-center mb-3">
                <h2 className="text-lg font-semibold text-gray-900 mb-1">
                  Question {currentQuestionIndex + 1} of {questions.length}
                </h2>
                <div className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800">
                  {getQuestionTypeLabel(currentQuestion.question_type)}
                </div>
              </div>

              {/* Compact Question Display */}
              <div className="mb-3">
                <div className="bg-gray-50 rounded-lg p-3 mb-2">
                  <h3 className="text-sm font-semibold text-gray-900 mb-1">
                    {currentQuestion.question_text}
                  </h3>
                  {currentQuestion.instruction_text && (
                    <p className="text-gray-600 text-xs">
                      <strong>Instructions:</strong> {currentQuestion.instruction_text}
                    </p>
                  )}
                </div>

                {/* Compact Tips */}
                <div className=" rounded-sm p-2 mb-3">
                  <h4 className="font-medium text-amber-900 mb-1 text-xs">Quick Tips:</h4>
                  <ul className="list-disc list-inside space-y-0.5 text-amber-800 text-xs">
                    <li>Good lighting & quiet environment</li>
                    <li>Position face in oval guide</li>
                    <li>Recording: {currentQuestion.recording_duration}s</li>
                  </ul>
                </div>
              </div>

              {/* Camera Preview Section */}
              {currentQuestion.question_type !== 'audio_only' && (
                <div className="mb-3">
                  <div className="relative bg-gray-900 rounded-lg overflow-hidden mx-auto max-w-md" style={{ aspectRatio: '4/3' }}>
                    {/* Always render video element so ref can attach */}
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full h-full object-cover"
                      style={{ 
                        transform: 'scaleX(-1)', // Mirror effect
                        display: (!cameraError && cameraReady) ? 'block' : 'none' // Hide until ready
                      }}
                    />
                    
                    {/* Face Detection Oval Frame - Shows red when face not detected */}
                    {cameraReady && !cameraError && (
                      <FaceDetectionOval 
                        showInstructions={false} // Hide instructions to save space
                        size="default"
                        orientation="vertical" // Vertical orientation for face recording
                        showCornerGuides={!recording} // Show corner guides when not recording
                        showCenterCrosshair={true} // Always show center crosshair for positioning
                        videoRef={videoRef} // Pass video reference for face detection
                        recording={recording}
                        recordingTime={recordingTime}
                      />
                    )}
                    
                    {/* Overlay states */}
                    {cameraError ? (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-900 text-white">
                        <div className="text-center">
                          <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-2" />
                          <p className="text-sm font-medium mb-2">Camera Access Failed</p>
                          <p className="text-xs text-gray-300 mb-3">{cameraError}</p>
                          <button
                            onClick={() => initializeCamera(0)}
                            className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition-colors"
                          >
                            Try Again
                          </button>
                        </div>
                      </div>
                    ) : !cameraReady ? (
                      <div className="absolute inset-0 flex items-center justify-center bg-gray-900 text-white">
                        <div className="text-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mx-auto mb-2"></div>
                          <p className="text-sm font-medium">Initializing Camera...</p>
                          <p className="text-xs text-gray-300 mt-1">Please allow camera access</p>
                        </div>
                      </div>
                    ) : null}
                    
                    {/* Camera ready indicator */}
                    {cameraReady && !cameraError && (
                      <div className="absolute top-2 left-2 flex items-center space-x-1">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-white text-xs font-medium">Ready</span>
                      </div>
                    )}
                    
                    {/* Recording indicator */}
                    {recording && cameraReady && !cameraError && (
                      <div className="absolute top-2 right-2 flex items-center space-x-1">
                        <div className="bg-red-600 text-white px-1.5 py-0.5 rounded-full text-xs font-medium flex items-center">
                          <div className="w-1 h-1 bg-white rounded-full mr-1 animate-pulse"></div>
                          REC
                        </div>
                        <div className="bg-black bg-opacity-70 text-white px-1.5 py-0.5 rounded-full text-xs font-medium">
                          {formatTime(recordingTime)}
                        </div>
                      </div>
                    )}

                    {/* Recording Controls Overlay */}
                    {cameraReady && !cameraError && (
                      <div className="absolute bottom-2 left-2 right-2">
                        <div className="flex justify-center space-x-2">
                          {/* Previous Button */}
                          <button
                            onClick={goToPreviousQuestion}
                            disabled={currentQuestionIndex === 0 || recording || uploading}
                            className="flex items-center justify-center px-2 py-1 bg-black bg-opacity-50 text-white rounded text-xs hover:bg-opacity-70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <ArrowLeft className="mr-1 h-3 w-3" />
                            Prev
                          </button>

                          {/* Record/Stop Button */}
                          {!recording ? (
                            <button
                              onClick={startRecording}
                              disabled={uploading}
                              className="flex items-center justify-center px-4 py-1 bg-red-600 text-white rounded font-semibold hover:bg-red-700 focus:outline-none focus:ring-1 focus:ring-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                              <Camera className="mr-1 h-4 w-4" />
                              Start
                            </button>
                          ) : (
                            <button
                              onClick={stopRecording}
                              disabled={uploading}
                              className="flex items-center justify-center px-4 py-1 bg-gray-600 text-white rounded font-semibold hover:bg-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                              {uploading ? (
                                <>
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                  Saving...
                                </>
                              ) : (
                                <>
                                  <Square className="mr-1 h-4 w-4" />
                                  Stop
                                </>
                              )}
                            </button>
                          )}

                          {/* Skip Button */}
                          <button
                            onClick={skipQuestion}
                            disabled={recording || uploading}
                            className="flex items-center justify-center px-2 py-1 bg-black bg-opacity-50 text-white rounded text-xs hover:bg-opacity-70 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <SkipForward className="mr-1 h-3 w-3" />
                            Skip
                          </button>
                  </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Compact status message */}
                  {cameraReady && !cameraError && (
                    <p className="text-center text-xs text-gray-600 mt-1">
                      {recording ? "Recording in progress..." : "Position face in oval & click Start"}
                    </p>
                  )}
                </div>
              )}

              {/* Audio-only message */}
              {currentQuestion.question_type === 'audio_only' && (
                <div className="mb-8">
                  <div className="bg-blue-50 rounded-lg p-6 text-center">
                    <Mic className="h-12 w-12 text-blue-600 mx-auto mb-4" />
                    <h4 className="text-lg font-medium text-blue-900 mb-2">Audio-Only Recording</h4>
                    <p className="text-blue-800">This question only requires audio recording. No camera preview is needed.</p>
                  </div>
                </div>
              )}

              {/* Frequency Visualization for Vital Signs Mode */}
              {vitalSignsMode && currentQuestion.question_type === 'audio_only' && (
                <div className="mb-8">
                  <div className="bg-gray-900 rounded-lg p-6">
                    <h4 className="text-white text-lg font-medium mb-4 text-center">
                      {currentQuestion.question_text.includes('pulse') ? "Pulse Frequency" : "Voice Frequency"}
                    </h4>
                    
                    {/* Real-time frequency bars */}
                    <div className="flex items-end justify-center space-x-1 h-32 mb-4">
                      {Array.from({ length: 20 }, (_, i) => {
                        const height = Math.random() * audioLevel + 10;
                        const getFrequencyColor = () => {
                          if (audioLevel > 100) return 'bg-green-500';
                          if (audioLevel > 50) return 'bg-yellow-500';
                          return 'bg-red-500';
                        };
                        return (
                          <div
                            key={i}
                            className={`w-2 rounded-t transition-all duration-100 ${getFrequencyColor()}`}
                            style={{ height: `${height}%` }}
                          />
                        );
                      })}
                    </div>

                    {/* Audio level indicator */}
                    <div className="bg-gray-800 rounded-full h-4 mb-4">
                      <div
                        className={`h-full rounded-full transition-all duration-200 ${
                          audioLevel > 100 ? 'bg-green-500' : audioLevel > 50 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min((audioLevel / 255) * 100, 100)}%` }}
                      />
                    </div>

                    {/* Frequency data */}
                    <div className="text-center">
                      <p className="text-white text-sm">
                        Audio Level: <span className="font-mono">{Math.round(audioLevel)}</span>
                      </p>
                      <p className="text-gray-400 text-xs">
                        {currentQuestion.question_text.includes('pulse') 
                          ? "Detecting pulse frequency from chest placement"
                          : "Analyzing voice frequency and pitch"
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Recording Controls */}
              {/* The recording controls are now integrated into the camera preview section */}
            </div>
          )}

          {/* Completion Step */}
          {step === 'complete' && finalVitals && (
            <div className="bg-white rounded-lg shadow-lg p-8">
              <div className="text-center mb-8">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-semibold text-gray-900 mb-2">Recording Complete!</h2>
                <p className="text-gray-600">
                  Your vital signs have been successfully processed from your {questions.length} recordings 
                  and sent to your healthcare provider.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                <div className="text-center p-6 bg-red-50 rounded-lg">
                  <Heart className="h-8 w-8 text-red-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{finalVitals.heart_rate}</p>
                  <p className="text-gray-600">Heart Rate (BPM)</p>
                </div>

                <div className="text-center p-6 bg-blue-50 rounded-lg">
                  <Timer className="h-8 w-8 text-blue-500 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{finalVitals.heart_rate_variability}</p>
                  <p className="text-gray-600">HRV (ms)</p>
                </div>

                <div className="text-center p-6 bg-purple-50 rounded-lg">
                  <div className="h-8 w-8 bg-purple-500 rounded-full mx-auto mb-2 flex items-center justify-center">
                    <span className="text-white text-sm font-bold">R</span>
                  </div>
                  <p className="text-lg font-bold text-gray-900">{finalVitals.rhythm}</p>
                  <p className="text-gray-600">Rhythm</p>
                </div>

                <div className="text-center p-6 bg-green-50 rounded-lg">
                  <div className="h-8 w-8 bg-green-500 rounded-full mx-auto mb-2 flex items-center justify-center">
                    <span className="text-white text-sm font-bold">Q</span>
                  </div>
                  <p className="text-lg font-bold text-gray-900">{finalVitals.signal_quality}</p>
                  <p className="text-gray-600">Signal Quality</p>
                </div>
              </div>

              <div className="text-center">
                <p className="text-gray-600 mb-4">
                  These results have been securely transmitted to your healthcare provider.
                </p>
                <button
                  onClick={() => window.close()}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-300 transition-colors"
                >
                  Close Window
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientRecordingNew;